




























import Vue from "vue";
import Component from "vue-class-component";
import { Prop } from "vue-property-decorator";
import { Notification, VentSettingModel } from "@/models";
import { formatVentNumber, formatVentUnits } from "@/filters/ventFields";
import { getVentSettingOrAlarmSettingLabel } from "./vent/vent-fields";

export interface SettingChange {
  [field: string]: string | undefined;
  name?: string;
  from?: string;
  to?: string;
  unit?: string;
}

@Component
export default class PatientNotification extends Vue {
  @Prop() notification!: Notification;

  isExpanded = false;

  get changes(): string[] {
    const message = this.notification?.message ?? "";
    const changes = this.parseSettingsChanges(message);

    return changes.map(this.getMessageForChange);
  }

  get message() {
    if (!this.changes || this.changes.length === 0) return "Vent Settings Changed";
    else if (this.changes.length === 1) return "1 Vent Setting Changed";
    else return `${this.changes.length} Vent Settings Changed`;
  }

  get canExpand() {
    return this.changes.length > 0;
  }

  parseSettingsChanges(message: string) {
    const matches = message.match(/\([^)]+\)/g);
    if (!matches) return [];

    return matches.map(match =>
      match
        // Remove the parenthesis from the match
        .replace(/[()]/g, "")
        // Split by comma to get the different fields
        .split(",")
        // Split the fields by colon to get the name and the value
        .map(field => field.split(":"))
        // Map the field values to an object using the field name as the property name
        .reduce((change, field) => {
          change[field[0].trim()] = field[1].trim();
          return change;
        }, {} as SettingChange)
    );
  }

  getMessageForChange(change: SettingChange) {
    // For mode, just show the values to save space and because users will be able to tell it's the mode
    if (change.name === "Mode") {
      return `${change.from} to ${change.to}`;
    }

    // These fields are in PascalCase whereas the vent fields for the VentSettingModel are in camelCase, so lowercase
    // the first character so the lookup will match
    const ventField = change.name ? change.name[0].toLocaleLowerCase() + change.name.substring(1) : "";
    const fieldLabel = getVentSettingOrAlarmSettingLabel(ventField as keyof VentSettingModel);

    const from = this.formatValue(change.from);
    const to = this.formatValue(change.to);

    if (change.unit) {
      return `${fieldLabel}: ${from} to ${to} ${formatVentUnits(change.unit)}`;
    } else {
      return `${fieldLabel}: ${from} to ${to}`;
    }
  }

  formatValue(value?: string) {
    if (!value) return value;

    const number = Number(value);
    return !isNaN(number) ? formatVentNumber(number) : value;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
  }
}
